<template>
  <bm-control v-bind="$attrs" :read-only-value="readOnlyValue">
    <v-menu
      ref="menuRef"
      v-model="showMenu"
      :close-on-content-click="false"
      :nudge-right="40"
      transition="scale-transition"
      offset-y
      :return-value="{ date, time }"
      @update:return-value="updateInstantFromControl"
    >
      <template #activator="{ on }">
        <v-text-field
          v-model="readOnlyValue"
          class="bm-text"
          :placeholder="placeholder"
          :prepend-icon="bmIconName('AccessTime')"
          readonly
          color="brand02"
          v-bind="$attrs"
          :clearable="clearable"
          :label="undefined"
          v-on="on"
        >
        </v-text-field>
      </template>
      <v-card :width="useHorizontalView ? '650px' : '350px'">
        <v-container fluid>
          <v-row v-if="useHorizontalView" class="primary">
            <v-col cols="6">
              <h2 class="white--text text-center">
                {{ formattedWorkingDate }}
              </h2>
            </v-col>
            <v-col cols="6">
              <h2 class="white--text text-center">
                {{ formattedWorkingTime }}
              </h2>
            </v-col>
          </v-row>
          <v-row class="px-2">
            <v-col :cols="useHorizontalView ? 6 : 12" class="text-center">
              <v-date-picker
                v-model="date"
                no-title
                scrollable
                color="brand02"
                :first-day-of-week="startDayOfWeek"
              />
            </v-col>
            <v-col :cols="useHorizontalView ? 6 : 12">
              <bm-time :value.sync="time" />
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-col cols="6" class="text-left pa-0">
            <bm-btn
              data-qa="cancel"
              icon
              x-large
              button-type="ghost"
              @click="showMenu = false"
            >
              <bm-icon icon="Cancel" />
            </bm-btn>
          </v-col>
          <v-col cols="6" class="text-right pa-0">
            <bm-btn
              data-qa="accept"
              icon
              x-large
              button-type="ghost"
              @click="menuRef.save({ date, time })"
            >
              <bm-icon icon="Accept" />
            </bm-btn>
          </v-col>
        </v-card-actions>
      </v-card>
    </v-menu>
  </bm-control>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmControl from "@/components/controls/input/BmControl.vue";
import type { Instant } from "@js-joda/core";
import { computed, ref, watch } from "vue";
import BmTime from "@/components/controls/input/BmTime.vue";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import { LocalDate, LocalTime } from "@js-joda/core";
import { useVuetify } from "@/plugins/vuetify";
import userStore from "@/store/modules/userStore";
import { screenSize } from "@/vue23switching/screenSize";
import { bmIconName } from "@/components/controls/bmIconMap";

interface BmDateTimeProps {
  value: Instant | null;
  placeholder?: string;
  clearable?: boolean;
}

interface BmDateTimeEmits {
  (e: "update:value", v: Instant | null): void;
}

const props = withDefaults(defineProps<BmDateTimeProps>(), {
  placeholder: " ",
});

const emit = defineEmits<BmDateTimeEmits>();

const { $vuetify } = useVuetify();

const menuRef = ref<any>(null);
const showMenu = ref(false);
const date = ref("");
const time = ref<LocalTime | null>(null);
const startDayOfWeek = userStore.state.user?.calendarStartDay.key ?? 1;

watch(
  () => props.value,
  (val) => {
    if (val) {
      date.value = tenantService.instantToDate(val).toString();
      time.value = tenantService.instantToTime(val);
    }
  },
  { immediate: true }
);

const readOnlyValue = computed({
  get: () => (props.value ? tenantService.formatDateTime(props.value) : null),
  set: (value) => {
    if (value === null) {
      emit("update:value", null);
    }
  },
});

const formattedWorkingDate = computed(() =>
  date.value ? tenantService.formatDate(LocalDate.parse(date.value)) : "-"
);

const formattedWorkingTime = computed(() =>
  time.value ? tenantService.formatTime(time.value) : "-"
);

const useHorizontalView = computed(() => screenSize.mdAndUp);

const updateInstantFromControl = (dateTime: {
  date: string | null;
  time: LocalTime | null;
}) => {
  emit(
    "update:value",
    tenantService.dateTimeToInstant(
      dateTime.date ? LocalDate.parse(dateTime.date) : LocalDate.now(),
      dateTime.time ?? LocalTime.now()
    )
  );
};
</script>
